import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { ExpandList } from '$partials/icons/index.js';
import { useKeyboard } from '$hooks/events';
import { useNavigation } from '$contexts/navigation.jsx';
import Button from '$partials/ui/button.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof modalPropTypes>} props
 */
const ProjectNavigationModal = ({
    close,
    currentProjectId,
    isOpen,
    links
}) => {
    const dialogRef = useRef();
    const dialogInnerRef = useRef();

    useKeyboard({
        'Escape': (e) => {
            e.preventDefault();
            isOpen && close();
        }
    }, [isOpen]);

    useEffect(() => {
        const dialogNode = dialogRef.current;
        if (isOpen) {
            if (dialogNode && !dialogNode.open) {
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open) {
                dialogNode.close?.();
            }
        }
    }, [isOpen]);

    return (
        <>
            <dialog
                className='dialog-reset'
                onClick={() => close()}
                ref={dialogRef}>
                <nav
                    aria-label='Project navigation'
                    className='project-navigation__modal'
                    onClick={(e) => e.stopPropagation()}
                    ref={dialogInnerRef}>
                    <ul>
                        {links.map((link, i) => {
                            return (
                                <li
                                    className={i > 0 ? 'mt-5' : ''}
                                    key={i}>
                                    <a
                                        className={link.id === currentProjectId ? '' : 'opacity-50'}
                                        href={link.url}>
                                        {link.title}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </dialog>
        </>
    );
};

/**
 * Persistant project navigation
 * @param {PropTypes.InferProps<typeof projectNavigationPropTypes>} props
 */
const ProjectNavigation = ({
    allProjects = [],
    currentProject,
    progress = 0,
}) => {
    const {
        backgroundColour,
        isOpen: isPrimaryNavigationOpen,
        textColour,
    } = useNavigation();

    const initialProject = useRef(currentProject);

    let sortedProjects = [];
    if (allProjects?.length > 0 && initialProject.current) {
        const initialProjectIndex = allProjects?.findIndex((p) => p.id === initialProject.current.id);
        sortedProjects = allProjects.slice(initialProjectIndex).concat(allProjects.slice(0, initialProjectIndex));
    }

    const [isOpen, setIsOpen] = useState(false);

    const progressWidth = `${(progress) * 100}%`;

    return (
        <>
            <style dangerouslySetInnerHTML={{
                __html: `
                    .project-navigation__button, .project-navigation__modal {
                        --background: ${backgroundColour};
                        --foreground: ${textColour};
                    }
                `}} />
            {(!isOpen && !isPrimaryNavigationOpen) &&
                <div
                    className='project-navigation__button'
                    style={{ clipPath: 'inset(1px 1px 1px 1px round 30px)' }}>
                    <Button
                        aria-label='Select a different project'
                        className='relative flex items-center w-full text-left px-[30px] bg-background text-foreground'
                        id='project-navigation'
                        onClick={() => setIsOpen(true)}
                        type='button'>
                        <span className='w-full min-w-0 pr-20'>
                            <span className='block truncate'>
                                {currentProject?.title}
                            </span>
                        </span>

                        <ExpandList />
                    </Button>

                    <div
                        className='absolute top-0 left-0 bottom-0 right-0 flex items-center bg-foreground text-background pointer-events-none px-[30px]'
                        style={{ clipPath: `inset(0 0 0 ${progressWidth})` }}>
                        <span className='w-full min-w-0 pr-20'>
                            <span className='block truncate'>
                                {currentProject?.title}
                            </span>
                        </span>

                        <ExpandList />
                    </div>
                </div>
            }

            {(isOpen && !isPrimaryNavigationOpen) &&
                <ProjectNavigationModal
                    close={() => setIsOpen(false)}
                    currentProjectId={currentProject?.id}
                    isOpen={isOpen}
                    links={sortedProjects} />
            }
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const modalPropTypes = {
    close: PropTypes.func.isRequired,
    isOpen: PropTypes.bool,
    currentProjectId: PropTypes.string.isRequired,
    links: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired
    }))
};
ProjectNavigationModal.propTypes = modalPropTypes;

const propTypes = {
    allProjects: PropTypes.array,
    currentProject: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    }),
    progress: PropTypes.number
};
ProjectNavigation.propTypes = propTypes;

export default ProjectNavigation;
